/**
 * DNA Ads AI - Sistema de Gestão de Campanhas
 * Criação, Otimização e Sugestões de Campanhas Publicitárias
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  Megaphone, Target, TrendingUp, DollarSign, Copy, Plus,
  RefreshCw, Sparkles, Play, Pause, Edit, Trash2, BarChart3,
  Instagram, Facebook, Youtube, Zap, Eye, ArrowUpRight,
  Users, MousePointer, CircleDollarSign, PieChart
} from 'lucide-react';

const OBJETIVO_CONFIG = {
  matriculas: { label: 'Matrículas', icon: Users, color: 'bg-purple-500' },
  reconhecimento: { label: 'Reconhecimento', icon: Eye, color: 'bg-blue-500' },
  engajamento: { label: 'Engajamento', icon: Target, color: 'bg-pink-500' },
  trafego: { label: 'Tráfego', icon: MousePointer, color: 'bg-green-500' },
  leads: { label: 'Leads', icon: Users, color: 'bg-orange-500' },
  eventos: { label: 'Eventos', icon: Megaphone, color: 'bg-cyan-500' }
};

const PLATAFORMA_CONFIG = {
  meta: { label: 'Meta (FB/IG)', icon: Facebook, color: 'text-blue-600' },
  google: { label: 'Google Ads', icon: Target, color: 'text-red-500' },
  tiktok: { label: 'TikTok', icon: Play, color: 'text-black' },
  youtube: { label: 'YouTube', icon: Youtube, color: 'text-red-600' }
};

const STATUS_CONFIG = {
  rascunho: { label: 'Rascunho', color: 'bg-gray-500' },
  pendente: { label: 'Pendente', color: 'bg-yellow-500' },
  ativa: { label: 'Ativa', color: 'bg-green-500' },
  pausada: { label: 'Pausada', color: 'bg-orange-500' },
  finalizada: { label: 'Finalizada', color: 'bg-blue-500' }
};

const PUBLICO_OPTIONS = [
  { value: 'pais_ed_infantil', label: 'Pais - Educação Infantil' },
  { value: 'pais_fundamental', label: 'Pais - Ensino Fundamental' },
  { value: 'pais_medio', label: 'Pais - Ensino Médio' },
  { value: 'jovens_vestibulandos', label: 'Jovens Vestibulandos' },
  { value: 'comunidade_local', label: 'Comunidade Local' }
];

export default function DnaAdsAI() {
  // Estados principais
  const [activeTab, setActiveTab] = useState('campanhas');
  const [campanhas, setCampanhas] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Estados de criação de campanha
  const [showNovaCampanha, setShowNovaCampanha] = useState(false);
  const [novaCampanha, setNovaCampanha] = useState({
    nome: '',
    objetivo: 'matriculas',
    plataformas: ['meta'],
    publico_alvo: ['pais_ed_infantil'],
    orcamento_diario: '',
    duracao_dias: '',
    descricao: ''
  });
  
  // Estados de geração de copy
  const [showGerarCopy, setShowGerarCopy] = useState(false);
  const [copyForm, setCopyForm] = useState({
    objetivo: 'matriculas',
    plataforma: 'meta',
    publico_alvo: ['pais_ed_infantil'],
    pontos_chave: '',
    tom: 'acolhedor'
  });
  const [copiesGeradas, setCopiesGeradas] = useState([]);
  const [gerandoCopy, setGerandoCopy] = useState(false);
  
  // Estados de sugestão de orçamento
  const [showSugerirOrcamento, setShowSugerirOrcamento] = useState(false);
  const [orcamentoForm, setOrcamentoForm] = useState({
    objetivo: 'matriculas',
    plataformas: ['meta'],
    duracao_dias: 30,
    meta_leads: '',
    regiao: 'Taboão da Serra'
  });
  const [orcamentoSugerido, setOrcamentoSugerido] = useState(null);
  const [calculandoOrcamento, setCalculandoOrcamento] = useState(false);
  
  // Carregar dados
  const carregarCampanhas = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/growth/ads/campanhas');
      setCampanhas(res.data.campanhas || []);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const carregarDashboard = useCallback(async () => {
    try {
      const res = await api.get('/growth/ads/dashboard');
      setDashboard(res.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    }
  }, []);
  
  useEffect(() => {
    carregarCampanhas();
    carregarDashboard();
  }, [carregarCampanhas, carregarDashboard]);
  
  // Handler de criação de campanha
  const handleCriarCampanha = async () => {
    if (!novaCampanha.nome) {
      toast.error('Informe o nome da campanha');
      return;
    }
    
    try {
      const payload = {
        ...novaCampanha,
        orcamento_diario: novaCampanha.orcamento_diario ? parseFloat(novaCampanha.orcamento_diario) : null,
        duracao_dias: novaCampanha.duracao_dias ? parseInt(novaCampanha.duracao_dias) : null
      };
      
      await api.post('/growth/ads/campanha', payload);
      toast.success('Campanha criada com sucesso!');
      setShowNovaCampanha(false);
      setNovaCampanha({
        nome: '',
        objetivo: 'matriculas',
        plataformas: ['meta'],
        publico_alvo: ['pais_ed_infantil'],
        orcamento_diario: '',
        duracao_dias: '',
        descricao: ''
      });
      carregarCampanhas();
      carregarDashboard();
    } catch (error) {
      console.error('Erro ao criar campanha:', error);
      toast.error('Erro ao criar campanha');
    }
  };
  
  // Handler de geração de copy
  const handleGerarCopy = async () => {
    try {
      setGerandoCopy(true);
      const payload = {
        ...copyForm,
        publico_alvo: copyForm.publico_alvo,
        pontos_chave: copyForm.pontos_chave ? copyForm.pontos_chave.split(',').map(p => p.trim()) : []
      };
      
      const res = await api.post('/growth/ads/gerar-copy', payload);
      setCopiesGeradas(res.data.copies || []);
      toast.success('Copies geradas com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar copy:', error);
      toast.error('Erro ao gerar copies');
    } finally {
      setGerandoCopy(false);
    }
  };
  
  // Handler de sugestão de orçamento
  const handleSugerirOrcamento = async () => {
    try {
      setCalculandoOrcamento(true);
      const payload = {
        ...orcamentoForm,
        meta_leads: orcamentoForm.meta_leads ? parseInt(orcamentoForm.meta_leads) : null
      };
      
      const res = await api.post('/growth/ads/sugerir-orcamento', payload);
      setOrcamentoSugerido(res.data);
      toast.success('Orçamento calculado!');
    } catch (error) {
      console.error('Erro ao sugerir orçamento:', error);
      toast.error('Erro ao calcular orçamento');
    } finally {
      setCalculandoOrcamento(false);
    }
  };
  
  // Handler de copiar texto
  const handleCopiar = (texto) => {
    navigator.clipboard.writeText(texto);
    toast.success('Copiado!');
  };
  
  // Handler de alteração de status
  const handleAlterarStatus = async (campanhaId, novoStatus) => {
    try {
      await api.put(`/growth/ads/campanha/${campanhaId}?status=${novoStatus}`);
      toast.success('Status atualizado!');
      carregarCampanhas();
      carregarDashboard();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status');
    }
  };
  
  // Componente de card de campanha
  const CampanhaCard = ({ campanha }) => {
    const objetivoConfig = OBJETIVO_CONFIG[campanha.objetivo] || OBJETIVO_CONFIG.matriculas;
    const statusConfig = STATUS_CONFIG[campanha.status] || STATUS_CONFIG.rascunho;
    const ObjetivoIcon = objetivoConfig.icon;
    
    return (
      <Card className="hover:shadow-md transition-shadow" data-testid={`campanha-card-${campanha.id}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-2 rounded-lg ${objetivoConfig.color}`}>
                  <ObjetivoIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-medium">{campanha.nome}</h4>
                  <p className="text-xs text-gray-500">{objetivoConfig.label}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {campanha.plataformas?.map(plat => {
                  const config = PLATAFORMA_CONFIG[plat];
                  if (!config) return null;
                  const Icon = config.icon;
                  return (
                    <span key={plat} className={`flex items-center gap-1 text-xs ${config.color}`}>
                      <Icon className="w-3 h-3" />
                      {config.label}
                    </span>
                  );
                })}
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-center mb-3">
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-500">Impressões</p>
                  <p className="font-medium">{campanha.metricas?.impressoes?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-500">Cliques</p>
                  <p className="font-medium">{campanha.metricas?.cliques?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-500">Leads</p>
                  <p className="font-medium">{campanha.metricas?.leads || 0}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <Badge className={`${statusConfig.color} text-white`}>
                  {statusConfig.label}
                </Badge>
                
                <div className="flex items-center gap-1">
                  {campanha.status === 'ativa' && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleAlterarStatus(campanha.id, 'pausada')}
                    >
                      <Pause className="w-4 h-4" />
                    </Button>
                  )}
                  {campanha.status === 'pausada' && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleAlterarStatus(campanha.id, 'ativa')}
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                  )}
                  {campanha.status === 'rascunho' && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleAlterarStatus(campanha.id, 'ativa')}
                    >
                      <Play className="w-4 h-4 text-green-600" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-orange-600" />
            DNA Ads AI
          </h1>
          <p className="text-gray-500 text-sm">Criação e Otimização de Campanhas com IA</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setShowGerarCopy(true)} data-testid="btn-gerar-copy">
            <Sparkles className="w-4 h-4 mr-2" />
            Gerar Copy
          </Button>
          
          <Button variant="outline" onClick={() => setShowSugerirOrcamento(true)} data-testid="btn-sugerir-orcamento">
            <DollarSign className="w-4 h-4 mr-2" />
            Sugerir Orçamento
          </Button>
          
          <Button onClick={() => setShowNovaCampanha(true)} data-testid="btn-nova-campanha">
            <Plus className="w-4 h-4 mr-2" />
            Nova Campanha
          </Button>
        </div>
      </div>
      
      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-purple-100">
                  <Megaphone className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Campanhas Ativas</p>
                  <p className="text-xl font-bold">{dashboard.campanhas_ativas || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Impressões</p>
                  <p className="text-xl font-bold">{dashboard.metricas?.total_impressoes?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Leads</p>
                  <p className="text-xl font-bold">{dashboard.metricas?.total_leads || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-orange-100">
                  <CircleDollarSign className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">CPL Médio</p>
                  <p className="text-xl font-bold">R$ {dashboard.taxas?.cpl?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="campanhas" className="flex items-center gap-2">
            <Megaphone className="w-4 h-4" />
            Campanhas
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Analytics
          </TabsTrigger>
        </TabsList>
        
        {/* Tab Campanhas */}
        <TabsContent value="campanhas">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Suas Campanhas</CardTitle>
                  <CardDescription>Gerencie suas campanhas de anúncios</CardDescription>
                </div>
                <Button variant="outline" size="icon" onClick={carregarCampanhas}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-orange-600" />
                </div>
              ) : campanhas.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma campanha criada</p>
                  <Button 
                    variant="link" 
                    className="mt-2"
                    onClick={() => setShowNovaCampanha(true)}
                  >
                    Criar primeira campanha
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {campanhas.map(campanha => (
                    <CampanhaCard key={campanha.id} campanha={campanha} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Tab Analytics */}
        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle>Performance Geral</CardTitle>
              <CardDescription>Métricas consolidadas de todas as campanhas</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600">
                      {dashboard.metricas?.total_cliques?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-500">Cliques Totais</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">
                      {dashboard.taxas?.ctr?.toFixed(2) || 0}%
                    </p>
                    <p className="text-sm text-gray-500">CTR Médio</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-600">
                      {dashboard.metricas?.total_conversoes || 0}
                    </p>
                    <p className="text-sm text-gray-500">Conversões</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-orange-600">
                      R$ {dashboard.metricas?.total_custo?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-500">Investido</p>
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Carregando dados...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Modal Nova Campanha */}
      <Dialog open={showNovaCampanha} onOpenChange={setShowNovaCampanha}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-orange-600" />
              Nova Campanha
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nome da Campanha *</Label>
              <Input 
                placeholder="Ex: Matrículas 2026 - Instagram"
                value={novaCampanha.nome}
                onChange={(e) => setNovaCampanha(prev => ({ ...prev, nome: e.target.value }))}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Objetivo</Label>
                <Select 
                  value={novaCampanha.objetivo}
                  onValueChange={(v) => setNovaCampanha(prev => ({ ...prev, objetivo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(OBJETIVO_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Orçamento Diário (R$)</Label>
                <Input 
                  type="number"
                  placeholder="Ex: 50"
                  value={novaCampanha.orcamento_diario}
                  onChange={(e) => setNovaCampanha(prev => ({ ...prev, orcamento_diario: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Duração (dias)</Label>
              <Input 
                type="number"
                placeholder="Ex: 30"
                value={novaCampanha.duracao_dias}
                onChange={(e) => setNovaCampanha(prev => ({ ...prev, duracao_dias: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea 
                placeholder="Descreva o objetivo e estratégia da campanha..."
                value={novaCampanha.descricao}
                onChange={(e) => setNovaCampanha(prev => ({ ...prev, descricao: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovaCampanha(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCriarCampanha}>
              <Plus className="w-4 h-4 mr-2" />
              Criar Campanha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal Gerar Copy */}
      <Dialog open={showGerarCopy} onOpenChange={setShowGerarCopy}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-orange-600" />
              Gerar Copy com IA
            </DialogTitle>
            <DialogDescription>
              A IA irá criar textos otimizados para seus anúncios
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Objetivo</Label>
                <Select 
                  value={copyForm.objetivo}
                  onValueChange={(v) => setCopyForm(prev => ({ ...prev, objetivo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(OBJETIVO_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Plataforma</Label>
                <Select 
                  value={copyForm.plataforma}
                  onValueChange={(v) => setCopyForm(prev => ({ ...prev, plataforma: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PLATAFORMA_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Pontos a destacar (separados por vírgula)</Label>
              <Input 
                placeholder="Ex: educação de qualidade, metodologia inovadora, ambiente acolhedor"
                value={copyForm.pontos_chave}
                onChange={(e) => setCopyForm(prev => ({ ...prev, pontos_chave: e.target.value }))}
              />
            </div>
            
            <Button 
              className="w-full"
              onClick={handleGerarCopy}
              disabled={gerandoCopy}
            >
              {gerandoCopy ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Gerar Copies
            </Button>
            
            {/* Copies geradas */}
            {copiesGeradas.length > 0 && (
              <div className="space-y-3">
                <Label>Copies Geradas</Label>
                {copiesGeradas.map((copy, index) => (
                  <Card key={index} className="p-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">{copy.titulo}</h4>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{copy.score}%</Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleCopiar(`${copy.titulo}\n\n${copy.texto_primario}\n\n${copy.texto_secundario}`)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">{copy.texto_primario}</p>
                      {copy.texto_secundario && (
                        <p className="text-sm text-gray-500">{copy.texto_secundario}</p>
                      )}
                      <Badge className="bg-blue-100 text-blue-700">
                        CTA: {copy.cta}
                      </Badge>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal Sugerir Orçamento */}
      <Dialog open={showSugerirOrcamento} onOpenChange={setShowSugerirOrcamento}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Sugerir Orçamento com IA
            </DialogTitle>
            <DialogDescription>
              A IA calculará um orçamento ideal baseado nos seus objetivos
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Objetivo</Label>
                <Select 
                  value={orcamentoForm.objetivo}
                  onValueChange={(v) => setOrcamentoForm(prev => ({ ...prev, objetivo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(OBJETIVO_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Duração (dias)</Label>
                <Input 
                  type="number"
                  value={orcamentoForm.duracao_dias}
                  onChange={(e) => setOrcamentoForm(prev => ({ ...prev, duracao_dias: parseInt(e.target.value) || 30 }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Meta de Leads (opcional)</Label>
              <Input 
                type="number"
                placeholder="Ex: 50"
                value={orcamentoForm.meta_leads}
                onChange={(e) => setOrcamentoForm(prev => ({ ...prev, meta_leads: e.target.value }))}
              />
            </div>
            
            <Button 
              className="w-full"
              onClick={handleSugerirOrcamento}
              disabled={calculandoOrcamento}
            >
              {calculandoOrcamento ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Calcular Orçamento
            </Button>
            
            {/* Resultado do orçamento */}
            {orcamentoSugerido && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Sugestão de Orçamento</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-gray-500">Mínimo/dia</p>
                      <p className="text-lg font-bold text-gray-600">
                        R$ {orcamentoSugerido.orcamento_minimo_diario?.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-green-100 rounded-lg p-2">
                      <p className="text-xs text-green-700">Recomendado/dia</p>
                      <p className="text-lg font-bold text-green-700">
                        R$ {orcamentoSugerido.orcamento_recomendado_diario?.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Ótimo/dia</p>
                      <p className="text-lg font-bold text-gray-600">
                        R$ {orcamentoSugerido.orcamento_otimo_diario?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="border-t pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Total Estimado:</span>
                      <span className="font-medium">R$ {orcamentoSugerido.orcamento_total_estimado?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>CPL Estimado:</span>
                      <span className="font-medium">R$ {orcamentoSugerido.cpl_estimado?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Leads Estimados:</span>
                      <span className="font-medium">{orcamentoSugerido.leads_estimados}</span>
                    </div>
                  </div>
                  
                  {orcamentoSugerido.justificativa && (
                    <p className="text-xs text-gray-600 border-t pt-3">
                      {orcamentoSugerido.justificativa}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
